import { DateTime } from 'luxon';

import { appLanguage } from 'services/appLanguage';
import i18n from '../i18n';

/**
 * ТЗ 0100: время занятия — ОДНО правило на весь LMS.
 *
 * Сервер отдаёт `startsAt`/`endsAt` — моменты с офсетом (время урока задаётся
 * по Варшаве). Студенту показываем его МЕСТНОЕ время (пояс браузера), а если
 * на этот момент оно расходится с варшавским — дописываем пометку пояса,
 * чтобы «19:00» в Киеве не путали с «19:00» школы.
 *
 * «Сегодня / завтра / занятие прошло» считаются по моментам; «сегодня» и
 * «завтра» — по местному календарю студента.
 *
 * Без `startsAt` (пустое или битое значение) — откат на дату занятия:
 * показываем только день, «прошло» — когда день уже позади. От СТАРОГО
 * сервера это не спасает: запрос с полем `startsAt` к схеме без него
 * получает 400 целиком, поэтому LMS выкатывается только после бэкенда.
 *
 * Раньше здесь была таблица TIME (+3 ч, наследие Минска) — удалена: после
 * миграции на сервере хранится варшавское время, сдвигать нечего.
 */
export const SCHOOL_ZONE = 'Europe/Warsaw';

/** ISO-момент → DateTime в поясе браузера; пустое или битое значение — null. */
export const parseLessonInstant = (value?: string | null): DateTime | null => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed : null;
};

/** Дата занятия (`YYYY-MM-DD`) как местный день; битое значение — null. */
const parseLessonDate = (value?: string | null): DateTime | null => {
  if (!value) return null;
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.startOf('day') : null;
};

/**
 * Пояс, который нужно назвать рядом со временем, или null.
 *
 * Сравниваем офсеты НА МОМЕНТ занятия, а не имена поясов: у студента в
 * Берлине часы совпадают с варшавскими круглый год, и пометка была бы шумом;
 * в Киеве или Лондоне — расходятся, и пометка обязательна.
 */
export const lessonZoneHint = (moment: DateTime | null): string | null => {
  if (!moment) return null;
  const local = moment.toLocal();
  return local.offset === moment.setZone(SCHOOL_ZONE).offset ? null : local.zoneName;
};

const withZoneHint = (text: string, moment: DateTime | null): string => {
  const zone = lessonZoneHint(moment);
  return zone ? `${i18n.t('lesson.time.localZone', { time: text, zone })}` : text;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** «Piątek, 2 października» — день недели и дата в родительном падеже. */
const formatLessonDay = (day: DateTime, locale: string) =>
  capitalize(day.setLocale(locale).toFormat('cccc, d MMMM'));

const HOURS = 'HH:mm';

/**
 * «Piątek, 2 października, 19:00–22:00» в поясе браузера, при чужом поясе —
 * «…, 19:00–22:00 (czas lokalny, Europe/Kyiv)».
 *
 * Без `startsAt` — только день по `date`; без всего — пустая строка.
 */
export const formatLessonRange = (
  startsAt?: string | null,
  endsAt?: string | null,
  locale?: string,
  date?: string | null,
): string => {
  const lang = locale || appLanguage();
  const start = parseLessonInstant(startsAt)?.toLocal() ?? null;
  if (!start) {
    const day = parseLessonDate(date);
    return day ? formatLessonDay(day, lang) : '';
  }
  const end = parseLessonInstant(endsAt)?.toLocal() ?? null;
  // Конец, равный началу, — занятие нулевой длины (правило сервера), а не
  // «до того же часа завтра»: показываем одно время, без «19:00–19:00».
  const hours =
    end && +end !== +start
      ? `${start.toFormat(HOURS)}–${end.toFormat(HOURS)}`
      : start.toFormat(HOURS);
  return withZoneHint(`${formatLessonDay(start, lang)}, ${hours}`, start);
};

/**
 * Начало занятия: «2 października 2026, 19:00» (карточка курса, «ближайшее
 * занятие»). Пустое/битое значение — пустая строка, а не «Invalid DateTime».
 */
export const formatLessonStart = (
  startsAt?: string | null,
  locale: string = appLanguage(),
): string => {
  const start = parseLessonInstant(startsAt)?.toLocal() ?? null;
  if (!start) return '';
  const text = start
    .setLocale(locale)
    .toLocaleString({ day: 'numeric', month: 'long', year: 'numeric' });
  return withZoneHint(`${text}, ${start.toFormat(HOURS)}`, start);
};

/** Короткое начало для подсказки у кнопки входа: «02.10, 19:00» (+ пояс). */
export const formatLessonStartShort = (start: DateTime | null): string =>
  start ? withZoneHint(start.toLocal().toFormat('dd.MM, HH:mm'), start) : '';

interface ILessonTimes {
  startsAt?: string | null;
  endsAt?: string | null;
  date?: string | null;
}

/** Местный день начала занятия (или дата занятия, если момента нет). */
const lessonLocalDay = ({ startsAt, date }: ILessonTimes): DateTime | null =>
  parseLessonInstant(startsAt)?.toLocal().startOf('day') ?? parseLessonDate(date);

/**
 * Занятие прошло: момент конца позади (без конца — начала). `endsAt` берём
 * как есть: сервер сам переносит конец через полночь, а конец, равный началу,
 * — занятие нулевой длины (прошло сразу после начала). Без моментов —
 * откат на дату: прошло, когда день занятия уже позади.
 */
export const isLessonOver = (lesson: ILessonTimes, now: DateTime = DateTime.now()): boolean => {
  const end = parseLessonInstant(lesson.endsAt) ?? parseLessonInstant(lesson.startsAt);
  if (end) return now > end;
  const day = parseLessonDate(lesson.date);
  return day ? day < now.toLocal().startOf('day') : false;
};

/** Занятие начинается сегодня по местному календарю студента. */
export const isLessonToday = (lesson: ILessonTimes, now: DateTime = DateTime.now()): boolean => {
  const day = lessonLocalDay(lesson);
  return Boolean(day && day.hasSame(now.toLocal(), 'day'));
};

/** Занятие начинается завтра по местному календарю студента. */
export const isLessonTomorrow = (lesson: ILessonTimes, now: DateTime = DateTime.now()): boolean => {
  const day = lessonLocalDay(lesson);
  return Boolean(day && day.hasSame(now.toLocal().plus({ days: 1 }), 'day'));
};
